/**
 * The one password rule this system has, mirrored from the server.
 *
 * The server is the boundary — `server/internal/core/crypto/policy.go`,
 * `ValidatePassword`. This exists so a person is told what is wrong while they
 * are still typing rather than after a round trip that returns a 422, which
 * makes it a **mirror**; and a mirror that checks less than the thing it mirrors
 * is worse than none at all, because it lets input through and the refusal then
 * lands somewhere the user was not looking. So every arm of the server's policy
 * is restated here, including the two degenerate cases.
 *
 * It lives in `@erpx/ui` rather than in an app because three surfaces need it —
 * the tenant app's invite drawer, the admin console's create-user and
 * set-password dialogs, and both apps' accept-invite/reset-password cards — and
 * `admin` cannot import from `web`. It was briefly mirrored in three places
 * (Go, `web/src/lib/password.ts`, `admin/src/lib/credentials.ts`) with the Go
 * comment pointing at a fourth that had never been written. Two hand-written
 * transcriptions of one rule agree on the day they are written and not
 * afterwards.
 *
 * Deliberately NOT a complexity matrix — see the server's own note on why NIST
 * SP 800-63B withdrew that recommendation. Length is the lever that pays.
 */

/** Matches `crypto.MinPasswordLength`. */
export const MIN_PASSWORD_LENGTH = 12

/** Matches `crypto.MaxPasswordLength`. Argon2id is not asked to hash a novel. */
export const MAX_PASSWORD_LENGTH = 256

/** Which arm of the policy a candidate breaks. Mirrors the server's error set. */
export type PasswordPolicyViolation = 'blank' | 'tooShort' | 'tooLong' | 'allSame'

/**
 * Which rule a candidate password breaks, or `null` when it breaks none.
 *
 * Counts code points rather than UTF-16 units, because the server counts runes:
 * a twelve-character Dhivehi passphrase is twelve characters to the person who
 * typed it, and `String.length` would disagree with the server about whether it
 * is long enough — in the direction that shows a false error on valid input.
 */
export function passwordPolicyViolation(password: string): PasswordPolicyViolation | null {
  if (password.trim() === '') return 'blank'

  const characters = [...password]
  if (characters.length < MIN_PASSWORD_LENGTH) return 'tooShort'
  if (characters.length > MAX_PASSWORD_LENGTH) return 'tooLong'
  if (characters.every((character) => character === characters[0])) return 'allSame'

  return null
}
